
import Alert from "@/components/Alert";
import FundamentalsSheet from "@/components/share/fund/FundamentalsSheet";
import FundTimeFrameSelector from "@/components/share/fund/FundTimeFrameSelector";
import ChartIcon from "@/components/share/icons/ChartIcon";
import TableIcon from "@/components/share/icons/TableIcon";
import fundamentalSheetConstants from '@/constants/fundamental.constants';
import { backendExceptionHandler, paginationStateHandler } from "@/mixins";
import _ from 'lodash';



    const {
        balanceSheetSections,
        keyMetricsSheetSections,
        incomeSheetSections,
        cashflowSheetSections,
        earningsSheetSections,
        balanceSheetFormatMapping,
        keyMetricsSheetFormatMapping,
        incomeSheetFormatMapping,
        cashflowSheetFormatMapping,
        earningsSheetFormatMapping,
        tabConstants,
    } = fundamentalSheetConstants;

    const fundamentalUrlToTabMapping = {
        kennzahlen: tabConstants.keyMetrics,
        'gewinn-und-verlust': tabConstants.incomeStatements,
        bilanz: tabConstants.balanceSheets,
        cashflow: tabConstants.cashFlow,
        ergebnis: tabConstants.earnings,
    }

    const fundamentalTabToUrlMapping = _.invert(fundamentalUrlToTabMapping);

    const fundamentalTabToApiMapping = {
        [tabConstants.keyMetrics]: 'keyMetrics',
        [tabConstants.incomeStatements]: 'incomeStatements',
        [tabConstants.balanceSheets]: 'balanceSheets',
        [tabConstants.cashFlow]: 'cashFlow',
        [tabConstants.earnings]: 'earnings',
    }

    export default {
        name: "ShareFundamentals",

        components: {
            Alert,
            FundamentalsSheet,
            FundTimeFrameSelector,
            ChartIcon,
            TableIcon,
        },

        mixins: [ backendExceptionHandler, paginationStateHandler ],
        

        computed: {
            isDisplayInThousands() {
                return this.activeTab !== tabConstants.earnings;
            },
            headers() {
                if (!this.fullHeaders) {
                    return []
                }
                return this.selectedTimeFrame == -1 ? this.fullHeaders : this.fullHeaders.slice(0, this.selectedTimeFrame + 1);
            },
        },

        props: {
            isin: {
                type: String
            },
            fundamentalTabId: {
                type: String,
                required: false,
            },
        },

        data() {
            return {
                share: null,
                activeRequest: null,
                open: true,
                activeTab: tabConstants.keyMetrics,
                errorMessage: null,
                tree: [],
                monthYearToggle: 0,
                isLoading: false,
                reportedCurrency: null,
                fullHeaders: [],
                isShowedFullYears: false,
                fundSheetRawData: [],
                tabConstants: tabConstants,
                balanceSheetSections: balanceSheetSections,
                keyMetricsSheetSections: keyMetricsSheetSections,
                incomeSheetSections: incomeSheetSections,
                cashflowSheetSections: cashflowSheetSections,
                earningsSheetSections: earningsSheetSections,
                balanceSheetFormatMapping: balanceSheetFormatMapping,
                keyMetricsSheetFormatMapping: keyMetricsSheetFormatMapping,
                incomeSheetFormatMapping: incomeSheetFormatMapping,
                cashflowSheetFormatMapping: cashflowSheetFormatMapping,
                earningsSheetFormatMapping: earningsSheetFormatMapping,
                selectedTimeFrame: 3,
                isTableView: false,
            }
        },

        watch: {
            fundamentalTabId: {
                immediate: true,
                handler(tabId) {
                    const originalTabId = this.$getOriginalTabId(tabId)
                    if (originalTabId && fundamentalUrlToTabMapping[originalTabId] !== undefined) {
                        this.activeTab = fundamentalUrlToTabMapping[originalTabId];
                    }
                }
            },
            activeTab: {
                immediate: true,
                handler(tab) {
                    const shareId = this.$route.params.share;
                    const tabId = fundamentalTabToUrlMapping[tab];

                    if (this.$route.params.tab_id) {
                        this.shareFundamentalsRequest(tab)
                    }
                    const translatedTabId = this.$getTranslatedTabId(tabId)

                    if (shareId && translatedTabId) {
                        this.$router.push({
                            name: this.$getTranslatedRouteName('aktie-share-fundamentaldaten-tab_id'),
                            params: {
                                share: shareId,
                                tab_id: translatedTabId
                            }
                        })
                    }
                }
            }
        },

        created() {
            this.selectedTimeFrame = this.$store.getters['preferences/getShareFundamentalTimeFrame']
            this.isTableView = this.$store.getters['preferences/getShareFundamentalIsTableView']
        },

        methods: {
            cancelActiveRequest() {
                if (this.activeRequest) {
                    this.activeRequest.cancel();
                    this.isLoading = false;
                }
            },
            shareFundamentalsRequest(tabIndex) {                
                const url = fundamentalTabToApiMapping[tabIndex];
                if (!url || !this.isin) {
                    return;
                }

                this.cancelActiveRequest();
                this.isLoading = true;

                const axiosSource = this.$axios.CancelToken.source();
                this.activeRequest = { cancel: axiosSource.cancel, msg: this.$t('component.share.shareFundamentals.info.loading') };

                return this
                    .$axios
                    .get(`/api/shares/fundamentals/${url}/${this.isin}?period=annual`, { cancelToken: axiosSource.token })
                    .then(response => {
                        this.share = response.data
                        this.reportedCurrency = this.share[0]?.reportedCurrency || null;
                        this.fullHeaders = this.share.map(period => ({
                            text: period.calendarYear,
                            value: period.calendarYear,
                        }))
                        this.fullHeaders.unshift({ text: this.$t('component.share.shareFundamentals.header.description'), value: 'metric' })
                        this.fundSheetRawData = this.share;
                        this.isLoading = false;
                        this.activeRequest = null
                    })
                    .catch(error => {
                        if (this.$axios.isCancel(error)) {
                            return;
                        }
                        this.isLoading = false;
                        this.activeRequest = null;
                        this.handleBackendException(error, this.$t('component.share.shareFundamentals.errors.financialDataLoadFailed') + error)
                    })
            },

            updateErrorMessage() {
                this.errorMessage = ""
            },

            handleChangeTimeFrame(timeFrame) {
                this.selectedTimeFrame = timeFrame
                this.$store.commit('preferences/setShareFundamentalTimeFrame', timeFrame)
            },
            handleChangeViewType(isTableView) {
                this.isTableView = isTableView
                this.$store.commit('preferences/setShareFundamentalIsTableView', isTableView)
            },
        },
    }
